<script>
import SelectContact from '../widgets/SelectContact.vue';
import SelectBranch from '../widgets/SelectBranch.vue';
import SelectProduct from '../widgets/SelectProduct.vue';
import Datepicker from 'vue2-datepicker';
import { format } from 'date-fns';

import { mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';

export default {
  name: "CreateBooingModal",
  components: {
    SelectContact,
    SelectBranch,
    SelectProduct,
    Datepicker,
  },
  data() {
    return {
      selectedBranchId: null,
      selectedContactId: null,
      selectedProduct: null,
      selectedQuantity: 1, 
      selectedDate: null,
      operatingDays: [],
      selectedRentalDuration: 1,
      selectedRentalUnit: 'day', 
      notes: '',
      isSubmitting: false,
      availableDates: [],
      calendarYear: new Date().getFullYear(),
      calendarMonth: new Date().getMonth() + 1, // JS months are 0-based, API expects 1-based
      rentalAvailability: null,
      checkingAvailability: false,
    }
  },
  methods: {
    ...mapActions('rentalBranch', ['createBooking', 'fetchAvailableDates', 'checkRentalAvailability']),
    async checkAvailability() {
      if (!this.selectedProduct || !this.selectedDate || !this.selectedRentalDuration || !this.selectedQuantity) {
        this.rentalAvailability = null;
        return;
      }
      this.checkingAvailability = true;
      try {
        const product_id = this.selectedProduct.product_id;
        const rental_date = typeof this.selectedDate === 'string' ? this.selectedDate : format(this.selectedDate, 'yyyy-MM-dd');
        const rental_duration = this.selectedRentalDuration;
        const quantity = this.selectedQuantity;
        const query = {
          product_id,
          rental_date,
          rental_duration,
          quantity
        };
        const response = await this.checkRentalAvailability({ query });
        this.rentalAvailability = response;
      } catch (err) {
        this.rentalAvailability = { available: false, message: err.message || 'Unable to check availability.' };
      } finally {
        this.checkingAvailability = false;
      }
    },
    onBranchSelected(branch) {
      this.selectedBranchId = branch.branch_id;
      this.operatingDays = branch.operating_days;
      this.selectedProduct = null; // Reset product when branch changes
      this.availableDates = [];
      this.rentalAvailability = null;
    },
    onContactSelected(contact) {
      // Accepts full contact object, expects id property
      this.selectedContactId = contact.id;
      this.rentalAvailability = null;
    },
    async onProductSelected(product) {
      this.selectedProduct = product;
      this.selectedDate = null;
      this.calendarYear = new Date().getFullYear();
      this.calendarMonth = new Date().getMonth() + 1;
      await this.fetchDatesForCurrentMonth();
      this.rentalAvailability = null;
    },
    async fetchDatesForCurrentMonth() {
      if (!this.selectedProduct || !this.selectedBranchId) {
        this.availableDates = [];
        return;
      }

      try {
        const quantity = this.selectedQuantity || 1;
        const product_id = this.selectedProduct.product_id;
        const now = new Date();

        const baseDate = new Date(this.calendarYear, this.calendarMonth - 1); // JS Date months are 0-based
        const monthsToFetch = [];

        const prevDate = new Date(baseDate.getFullYear(), baseDate.getMonth() - 1);
        if (prevDate >= new Date(now.getFullYear(), now.getMonth(), 1)) {
          monthsToFetch.push({ year: prevDate.getFullYear(), month: prevDate.getMonth() + 1 });
        }

        monthsToFetch.push({ year: baseDate.getFullYear(), month: baseDate.getMonth() + 1 });

        const nextDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1);
        monthsToFetch.push({ year: nextDate.getFullYear(), month: nextDate.getMonth() + 1 });

        const allDates = [];

        for (const { year, month } of monthsToFetch) {
          const query = { product_id, quantity, year, month };
          const response = await this.fetchAvailableDates({ query });
          allDates.push(...(response.available_dates || []));
        }

        this.availableDates = allDates;
      } catch (err) {
        this.availableDates = [];
      }
    },
    onCalendarChange(date) {
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // Note: 0-based index

      this.calendarYear = year;
      this.calendarMonth = month;
      this.fetchDatesForCurrentMonth();
    },
    disableUnavailableDays(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // normalize today to 00:00
      const isBeforeToday = date < today;
      // Only allow dates in availableDates
      const formatted = format(date, 'yyyy-MM-dd');
      const isAvailable = this.availableDates.includes(formatted);
      return isBeforeToday || !isAvailable;
    },
    getToday() {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d;
    },
    async handleSubmit() {
      if (!this.selectedBranchId || !this.selectedContactId || !this.selectedProduct || !this.selectedDate) {
        useAlert('Please fill in all required fields.');
        return;
      }
      this.isSubmitting = true;
      try {
        const payload = {
          branch_id: this.selectedBranchId,
          currency: (this.selectedProduct.pricing && this.selectedProduct.pricing[0]?.currency) || 'SGD',
          customer_id: this.selectedContactId,
          customer_notes: this.notes,
          product_id: this.selectedProduct.product_id,
          quantity_rented: this.selectedQuantity,
          rental_date: typeof this.selectedDate === 'string'
            ? this.selectedDate
            : format(this.selectedDate, 'yyyy-MM-dd'),
          rental_duration: this.selectedRentalDuration,
          rental_unit: this.selectedRentalUnit
        };
        await this.createBooking({ payload });
        useAlert('Booking created successfully!');
        this.$emit('close');
      } catch (err) {
        useAlert(err.message || 'Failed to create booking.');
      } finally {
        this.isSubmitting = false;
      }
    },
    handleClose() {
      this.$emit('close');
    }
  },
  computed: {
    allowedDays() {
      const dayMap = {
        Sun: 0,
        Mon: 1,
        Tue: 2,
        Wed: 3,
        Thu: 4,
        Fri: 5,
        Sat: 6
      }
      return this.operatingDays.split(',').map(day => dayMap[day.trim()])
    },
    bookingCheckFields() {
      // Use stringified date for comparison to ensure watcher triggers
      return [
        this.selectedProduct ? this.selectedProduct.product_id : '',
        this.selectedDate ? (typeof this.selectedDate === 'string' ? this.selectedDate : format(this.selectedDate, 'yyyy-MM-dd')) : '',
        this.selectedRentalDuration,
        this.selectedQuantity
      ].join('|');
    }
  },
  watch: {
    selectedProduct(newVal) {
      if (newVal) {
        this.selectedQuantity = 1;
      }
    },
    selectedQuantity(newVal, oldVal) {
      if (this.selectedProduct && newVal !== oldVal) {
        this.selectedDate = null; 
        this.fetchDatesForCurrentMonth();
      }
      this.rentalAvailability = null;
    },
    bookingCheckFields: {
      handler() {
        this.checkAvailability();
      },
      immediate: false
    },
    operatingDays(newVal) {
      this.selectedDate = null;
    }
  }
}
</script>

<template>
  <div class="z-50 fixed inset-0 flex items-center bg-slate-800/40 px-4">
    <div class="max-h-[80vh] overflow-y-auto mx-auto my-2 p-6 border-2 rounded-lg bg-white dark:bg-slate-800 shadow-xl min-w-[350px] w-full max-w-md relative">
      <button @click="handleClose" class="absolute top-2 right-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-xl">&times;</button>
      <h2 class="text-xl font-semibold mb-4 text-slate-900 dark:text-slate-100">Create Booking</h2>
      <SelectContact @selected="onContactSelected" />
      <SelectBranch @selected="onBranchSelected" />
      <SelectProduct :selected-branch-id="selectedBranchId" @selected="onProductSelected" />
      <div>
        <label for="numberInput">Select Quantity</label>
        <input
          id="numberInput"
          type="number"
          v-model="selectedQuantity"
          :min="1"
          :max="selectedProduct ? selectedProduct.quantity : 10"
          class="border px-2 py-1 rounded"
        />
      </div>
      <div>
        <label for="datepicker">Select date:</label>
        <Datepicker
          id="datepicker"
          v-model="selectedDate"
          :disabled-date="disableUnavailableDays"
          format="YYYY-MM-DD"
          :min-date="getToday()"
          class="w-full"
          @calendar-change="onCalendarChange"
        />
      </div>
      <div>
        <label>Select Rental Duration</label>
        <div class="flex items-center gap-2">
          <input
            type="number"
            v-model="selectedRentalDuration"
            min="1"
            class="w-20 px-2 py-1 border rounded"
          />
          <select
            v-model="selectedRentalUnit"
            class="px-2 py-1 border rounded"
          >
            <option value="day">Day(s)</option>
            <!-- <option value="week">Week(s)</option>
            <option value="month">Month(s)</option> -->
          </select>
        </div>
      </div>
      <div>
        <label for="notes">Customer Notes:</label>
        <textarea
          id="notes"
          v-model="notes"
          class="w-full px-2 py-1 border rounded"
          rows="3"
        ></textarea>
      </div>
      <div v-if="selectedProduct && selectedDate && selectedRentalDuration && selectedQuantity">
        <span v-if="checkingAvailability" class="text-blue-500 text-sm">Checking availability...</span>
        <span v-else-if="rentalAvailability" :class="{'text-green-600': rentalAvailability.available, 'text-red-600': !rentalAvailability.available }" class="text-sm">
          {{ rentalAvailability.message }}
        </span>
      </div>
      <div class="flex justify-end mt-4">
        <button @click="handleSubmit" :disabled="isSubmitting || !rentalAvailability || !rentalAvailability.available" class="px-4 py-2 rounded bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-60">
          {{ isSubmitting ? 'Creating...' : 'Create Booking' }}
        </button>
      </div>
    </div>
  </div>
</template>